import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  MenuItem,
  Tooltip,
  Alert,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { CacheKey } from "../../../types/cache-keys";
import ResourceRelationPanel from "../Panels/ResourceRelationPanel";
import TagManagementSection from "../Common/TagManagementSection";
import SnackableCourseButton from "../SnackableCourse/SnackableCourseButton";
import SnackableCourseManager from "../SnackableCourse/SnackableCourseManager";
import ProductFeatureSuccessCriteriaSection from "./ProductFeatureSuccessCriteriaSection";
import {
  ProductFeatureVersionLink,
  useAddProductFeatureMutation,
  useAddProductFeatureVersionMutation,
  useDeleteProductFeatureVersionMutation,
  useFetchLicenses,
  useFetchProductFeatureVersions,
  useFetchProductVersions,
  useFetchSingleProduct,
  useFetchSingleProductFeature,
  useUpdateProductFeatureMutation,
} from "../../../hooks/product-hooks";
import { useAttachedItemsUpdateMutation } from "../../../hooks/gtm-solution-hooks";
import {
  ProductFeature,
  ProductLicense,
  ProductVersion,
} from "../../../types/product";
import { asArray } from "../../../support/utils";
import { VersionScope } from "../../../types/snackableCourse";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  productId: number;
  featureId?: number;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
  versionScope?: VersionScope | null;
};

type FeatureVersionDraftRow = {
  rowId: string;
  joinId?: number | null;
  versionId: number | null;
  licenseId: number | null;
};

const makeRowId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `feature-version-row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const toId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === "object" && value !== null && "id" in value) {
    return toId((value as { id?: unknown }).id);
  }

  return null;
};

const normalizeApiItems = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.records)) return value.records;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const getJoinFeatureId = (join: ProductFeatureVersionLink): number | null => {
  return (
    toId((join as any)?.product_feature) ||
    toId((join as any)?.feature) ||
    null
  );
};

const joinBelongsToFeature = (
  join: ProductFeatureVersionLink,
  targetFeatureId?: number | null,
): boolean => {
  const joinFeatureId = getJoinFeatureId(join);

  if (targetFeatureId == null || joinFeatureId == null) return false;

  return Number(joinFeatureId) === Number(targetFeatureId);
};

const getJoinVersionId = (join: ProductFeatureVersionLink): number | null => {
  return toId((join as any)?.version);
};

const getJoinLicenseId = (join: ProductFeatureVersionLink): number | null => {
  return (
    toId((join as any)?.license) ||
    toId((join as any)?.product_license) ||
    null
  );
};

const getVersionLabel = (version: any): string => {
  return String(version?.version || version?.name || version?.version_name || version?.id || "").trim();
};

const sortVersionsForDisplay = (versions: ProductVersion[] = []) => {
  return [...versions].sort((a: any, b: any) => {
    const aReleased = a?.released ? new Date(a.released).getTime() : 0;
    const bReleased = b?.released ? new Date(b.released).getTime() : 0;

    if (aReleased !== bReleased) {
      return bReleased - aReleased;
    }

    return String(b?.version ?? "").localeCompare(String(a?.version ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
};

const getProductScopedLicenses = ({
                                    product,
                                    allLicenses,
                                  }: {
  product: any;
  allLicenses: ProductLicense[];
}): ProductLicense[] => {
  const fromProduct = Array.isArray(product?.license)
    ? product.license
    : Array.isArray(product?.licenses)
      ? product.licenses
      : [];

  if (fromProduct.length) {
    return fromProduct.filter(Boolean);
  }

  const fromFeatures = Array.isArray(product?.feature)
    ? product.feature.flatMap((feature: any) => {
      if (Array.isArray(feature?.license)) return feature.license;
      if (Array.isArray(feature?.licenses)) return feature.licenses;
      return [];
    })
    : [];

  const uniqueById = new Map<number, ProductLicense>();

  fromFeatures.forEach((license: any) => {
    const id = toId(license);
    if (id == null) return;

    if (typeof license === "object" && license !== null) {
      uniqueById.set(id, license as ProductLicense);
      return;
    }

    const found = allLicenses.find((item) => Number(item.id) === Number(id));
    if (found) uniqueById.set(id, found);
  });

  if (uniqueById.size) {
    return Array.from(uniqueById.values());
  }

  return allLicenses ?? [];
};

const serializeRows = (rows: FeatureVersionDraftRow[]) => {
  return JSON.stringify(
    rows
      .filter((row) => row.versionId != null)
      .map((row) => ({
        versionId: row.versionId ?? null,
        licenseId: row.licenseId ?? null,
      }))
      .sort((a, b) => {
        const aKey = `${a.versionId ?? ""}-${a.licenseId ?? ""}`;
        const bKey = `${b.versionId ?? ""}-${b.licenseId ?? ""}`;
        return aKey.localeCompare(bKey);
      }),
  );
};

export default function ProductFeatureEditorDialog({
                                                     open,
                                                     mode,
                                                     productId,
                                                     featureId,
                                                     onClose,
                                                     onSaved,
                                                     readOnly = false,
                                                     versionScope = null,
                                                   }: Props) {
  const isEdit = mode === "edit" && !!featureId;

  const { data: product, isLoading: productLoading } = useFetchSingleProduct(productId);
  const { data: featureData, isLoading: featureLoading } = useFetchSingleProductFeature(productId, featureId);

  const feature: ProductFeature | null = useMemo(() => {
    if (isEdit) {
      if (productLoading && featureLoading) return null;
      if (featureData) return featureData;
      const found = asArray(product?.feature).find((s) => Number(s.id) === Number(featureId));
      return found ?? null;
    }

    return null;
  }, [featureId, productLoading, featureData, featureLoading, product?.feature, isEdit]);

  const { mutateAsync: addFeature, isPending: addingFeature } = useAddProductFeatureMutation(productId);
  const { mutateAsync: updateFeature, isPending: updatingFeature } = useUpdateProductFeatureMutation(productId);

  const { mutateAsync: updateLicenseJoins, isPending: updatingLicenseJoins } =
    useAttachedItemsUpdateMutation<ProductFeature, ProductLicense>({
      type: CacheKey.ProductFeatureLicense,
      productId,
    });

  const addFeatureVersion = useAddProductFeatureVersionMutation(productId);
  const deleteFeatureVersion = useDeleteProductFeatureVersionMutation(productId);

  const [form, setForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    why_its_important: "",
    how_to_sell: "",
  });

  const [initialForm, setInitialForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    why_its_important: "",
    how_to_sell: "",
  });

  const { data: licenses = [], isLoading: licensesLoading } = useFetchLicenses();

  const normalizedLicenses = useMemo(() => {
    return normalizeApiItems<ProductLicense>(licenses);
  }, [licenses]);

  const licensesById = useMemo(() => {
    const map = new Map<ProductLicense["id"], ProductLicense>();
    for (const license of normalizedLicenses ?? []) map.set(license.id, license);
    return map;
  }, [normalizedLicenses]);

  const scopedProductLicenses = useMemo(() => {
    return getProductScopedLicenses({
      product,
      allLicenses: normalizedLicenses ?? [],
    });
  }, [product, normalizedLicenses]);

  const [featureLicenses, setFeatureLicenses] = useState<ProductLicense[]>([]);
  const availableLicenses = useMemo(() => {
    if (licensesLoading) return [];
    const assignedIds = new Set(featureLicenses.map((l) => l.id));
    return (scopedProductLicenses ?? []).filter((l) => !assignedIds.has(l.id));
  }, [scopedProductLicenses, featureLicenses, licensesLoading]);

  const [selectedLicense, setSelectedLicense] = useState<any | null>(null);
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);

  const { data: allProductVersionsRaw = [], isLoading: productVersionsLoading } =
    useFetchProductVersions(productId, {}, { enabled: open && !!productId });

  const {
    data: featureVersionsRaw = [],
    isLoading: featureVersionsLoading,
    refetch: refetchFeatureVersions,
  } = useFetchProductFeatureVersions(
    featureId,
    { detailed: true, size: 2000 },
    { enabled: open && isEdit && !!featureId },
  );

  const allProductVersions = useMemo(() => {
    return normalizeApiItems<ProductVersion>(allProductVersionsRaw);
  }, [allProductVersionsRaw]);

  const allFeatureVersionLinks = useMemo(() => {
    return normalizeApiItems<ProductFeatureVersionLink>(featureVersionsRaw);
  }, [featureVersionsRaw]);

  const currentFeatureVersionLinks = useMemo(() => {
    if (!isEdit || !featureId) return [];

    return allFeatureVersionLinks.filter((join) => joinBelongsToFeature(join, featureId));
  }, [allFeatureVersionLinks, featureId, isEdit]);

  const sortedProductVersions = useMemo(() => {
    return sortVersionsForDisplay(allProductVersions ?? []);
  }, [allProductVersions]);

  const [versionRows, setVersionRows] = useState<FeatureVersionDraftRow[]>([]);
  const [initialVersionRows, setInitialVersionRows] = useState<FeatureVersionDraftRow[]>([]);

  const selectedVersionIds = useMemo(() => {
    return new Set(
      versionRows
        .map((row) => row.versionId)
        .filter((id): id is number => id != null),
    );
  }, [versionRows]);

  const effectiveVersionScope: VersionScope | null = useMemo(() => {
    if (versionScope?.product_version_id || versionScope?.doc_version) {
      return {
        ...versionScope,
        product_id: versionScope.product_id ?? productId,
      };
    }

    return null;
  }, [versionScope, productId]);

  useEffect(() => {
    if (!open) return;

    if (!isEdit || !feature) {
      const emptyForm = {
        name: "",
        short_description: "",
        description: "",
        why_its_important: "",
        how_to_sell: "",
      };

      setForm(emptyForm);
      setInitialForm(emptyForm);
      setFeatureLicenses([]);
      setVersionRows([]);
      setInitialVersionRows([]);
      return;
    }

    const nextForm = {
      name: feature.name ?? "",
      short_description: feature.short_description ?? "",
      description: feature.description ?? "",
      why_its_important: (feature as any).why_its_important ?? "",
      how_to_sell: (feature as any).how_to_sell ?? "",
    };

    setForm(nextForm);
    setInitialForm(nextForm);
    setFeatureLicenses(feature.license ?? []);
  }, [open, isEdit, feature, mode]);

  useEffect(() => {
    if (!open || !isEdit) return;

    const rows: FeatureVersionDraftRow[] = (currentFeatureVersionLinks ?? [])
      .map((join: ProductFeatureVersionLink) => {
        const versionId = getJoinVersionId(join);
        if (versionId == null) return null;

        return {
          rowId: String((join as any).id ?? makeRowId()),
          joinId: toId((join as any).id),
          versionId,
          licenseId: getJoinLicenseId(join),
        };
      })
      .filter(Boolean) as FeatureVersionDraftRow[];

    setVersionRows(rows);
    setInitialVersionRows(rows);
  }, [open, isEdit, currentFeatureVersionLinks]);

  const canManageRelations = isEdit;

  const saving =
    addingFeature ||
    updatingFeature ||
    updatingLicenseJoins ||
    addFeatureVersion.isPending ||
    deleteFeatureVersion.isPending;

  const loading =
    featureLoading ||
    productLoading ||
    productVersionsLoading ||
    featureVersionsLoading;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.short_description !== initialForm.short_description ||
      form.description !== initialForm.description ||
      form.why_its_important !== initialForm.why_its_important ||
      form.how_to_sell !== initialForm.how_to_sell ||
      serializeRows(versionRows) !== serializeRows(initialVersionRows)
    );
  }, [
    form,
    initialForm,
    versionRows,
    initialVersionRows,
  ]);

  const getAvailableVersionsForRow = (rowId: string) => {
    const currentRow = versionRows.find((row) => row.rowId === rowId);
    const currentVersionId = currentRow?.versionId ?? null;

    return (sortedProductVersions ?? []).filter((version: any) => {
      const versionId = Number(version.id);
      if (currentVersionId != null && versionId === Number(currentVersionId)) return true;
      return !selectedVersionIds.has(versionId);
    });
  };

  const canAddVersionRow = useMemo(() => {
    const totalVersions = (sortedProductVersions ?? []).length;
    const selectedCount = versionRows.filter((row) => row.versionId != null).length;
    return selectedCount < totalVersions;
  }, [sortedProductVersions, versionRows]);

  const addVersionRow = () => {
    if (readOnly) return;

    setVersionRows((prev) => [
      ...prev,
      {
        rowId: makeRowId(),
        joinId: null,
        versionId: null,
        licenseId: null,
      },
    ]);
  };

  const removeVersionRow = (rowId: string) => {
    if (readOnly) return;
    setVersionRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const updateVersionRow = (rowId: string, patch: Partial<FeatureVersionDraftRow>) => {
    if (readOnly) return;

    setVersionRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
            ...row,
            ...patch,
          }
          : row,
      ),
    );
  };

  const syncFeatureVersions = async (targetFeatureId: number) => {
    const desiredRows = versionRows
      .filter((row) => row.versionId != null)
      .map((row) => ({
        versionId: Number(row.versionId),
        licenseId: row.licenseId ?? null,
      }));

    const currentJoins = isEdit && Number(featureId) === Number(targetFeatureId)
      ? currentFeatureVersionLinks ?? []
      : [];

    await Promise.all(
      currentJoins
        .filter((join: any) => !!join?.id)
        .map((join: any) => deleteFeatureVersion.mutateAsync({ id: join.id } as any)),
    );

    await Promise.all(
      desiredRows.map(({ versionId, licenseId }) =>
        addFeatureVersion.mutateAsync({
          product_feature: targetFeatureId,
          feature: targetFeatureId,
          version: versionId,
          license: licenseId,
          product_license: licenseId,
        } as any),
      ),
    );

    if (isEdit) {
      await refetchFeatureVersions();
    }
  };

  const handleSave = async () => {
    if (readOnly) {
      handleCloseDialog();
      return;
    }

    let finalFeatureId = featureId ?? null;

    if (mode === "create") {
      const created: any = await addFeature({ ...form, product: productId });
      finalFeatureId = created?.id ?? null;
    } else if (isEdit && featureId) {
      await updateFeature({ ...form, id: featureId, product: productId });
      finalFeatureId = featureId;
    }

    if (finalFeatureId) {
      await syncFeatureVersions(finalFeatureId);
    }

    onSaved();
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setForm({
      name: "",
      short_description: "",
      description: "",
      why_its_important: "",
      how_to_sell: "",
    });
    setInitialForm({
      name: "",
      short_description: "",
      description: "",
      why_its_important: "",
      how_to_sell: "",
    });
    setSelectedLicense(null);
    setVersionRows([]);
    setInitialVersionRows([]);
    onClose();
  };

  const handleDeleteLicense = async (licenseId: number) => {
    const selectedItems = new Set(featureLicenses.map((l) => l.id));
    selectedItems.delete(licenseId);

    await updateLicenseJoins({
      selectedItems,
      attachingToId: featureId,
    });

    setFeatureLicenses((prev) => prev.filter((license) => license.id !== licenseId));
  };

  const handleAttachLicense = async (selectedLicenseId: number | string) => {
    if (!featureId || !selectedLicenseId) return;

    const selectedItems = new Set(featureLicenses.map((l) => l.id));
    selectedItems.add(Number(selectedLicenseId));

    await updateLicenseJoins({
      selectedItems,
      attachingToId: featureId,
    });

    const selected = licensesById.get(Number(selectedLicenseId));
    if (selected) {
      setFeatureLicenses((prev) => {
        if (prev.some((license) => Number(license.id) === Number(selected.id))) return prev;
        return [...prev, selected];
      });
    }

    setSelectedLicense(null);
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {readOnly
                ? mode === "create"
                  ? "View Product Feature"
                  : `View Feature: ${form?.name || `#${featureId}`}`
                : mode === "create"
                  ? "Create Product Feature"
                  : `Feature: ${form?.name || `#${featureId}`}`}
            </Typography>

            {isEdit && (
              <SnackableCourseButton
                entityType="product_feature"
                entityId={featureId}
                entityName={form.name}
                disabled={!isEdit}
                versionScope={effectiveVersionScope}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading && (
            <Box display="flex" alignItems="center" gap={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}

          <Box>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={form.name ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
              disabled={readOnly}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Short Description"
              value={form.short_description ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, short_description: e.target.value }))}
              multiline
              minRows={2}
              disabled={readOnly}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Description"
              value={form.description ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
              multiline
              minRows={3}
              disabled={readOnly}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Why It's Important"
              value={form.why_its_important ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, why_its_important: e.target.value }))}
              multiline
              minRows={3}
              helperText="The customer value. Shown on the feature card in the architecture page's products section."
              disabled={readOnly}
            />

            <TextField
              fullWidth
              margin="normal"
              label="How To Sell"
              value={form.how_to_sell ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, how_to_sell: e.target.value }))}
              multiline
              minRows={3}
              helperText="The seller's angle. Shown in the architecture page's feature detail modal."
              disabled={readOnly}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Versions containing this feature ({versionRows.filter((row) => row.versionId != null).length})
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Add one row per version where this feature is available. Optionally select the license that applies to the feature/version mapping.
                </Typography>
              </Box>

              {!readOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addVersionRow}
                  disabled={saving || productVersionsLoading || !canAddVersionRow}
                >
                  + Add Version
                </Button>
              )}
            </Box>

            <Box display="grid" gap={2}>
              {versionRows.map((row, index) => {
                const availableVersions = getAvailableVersionsForRow(row.rowId);

                return (
                  <Box
                    key={row.rowId}
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr auto" }}
                    gap={2}
                    alignItems="start"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label={`Version ${index + 1}`}
                      value={row.versionId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateVersionRow(row.rowId, {
                          versionId: value === "" ? null : Number(value),
                          licenseId: null,
                        });
                      }}
                      disabled={readOnly || productVersionsLoading}
                    >
                      <MenuItem value="">
                        <em>Select version</em>
                      </MenuItem>

                      {availableVersions.map((version: ProductVersion) => (
                        <MenuItem key={version.id} value={version.id}>
                          {getVersionLabel(version)}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="License"
                      value={row.licenseId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateVersionRow(row.rowId, {
                          licenseId: value === "" ? null : Number(value),
                        });
                      }}
                      disabled={readOnly || licensesLoading || row.versionId == null}
                      helperText="Optional product license"
                    >
                      <MenuItem value="">
                        <em>No license</em>
                      </MenuItem>

                      {(scopedProductLicenses ?? []).map((license: ProductLicense) => (
                        <MenuItem key={license.id} value={license.id}>
                          {license.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {!readOnly && (
                      <Tooltip title="Remove">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeVersionRow(row.rowId)}
                            disabled={saving}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}

              {!versionRows.length && (
                <Alert severity="info">
                  No versions are currently mapped to this feature. Click “+ Add Version” to add one.
                </Alert>
              )}
            </Box>

            {!productVersionsLoading && sortedProductVersions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No product versions exist yet. Create product versions before assigning feature availability.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box mt={2}>
            <ResourceRelationPanel
              joinEndpoint="db/product-feature-to-resource"
              joinCacheKey={CacheKey.ProductFeatureToResource}
              parentField="product_feature"
              parentId={featureId ?? null}
              title="Resources"
              showNameColumn={false}
              maxHeight={300}
              invalidateKeys={[CacheKey.ProductFeature]}
              disabled={!canManageRelations}
              readOnly={readOnly}
              versionScope={effectiveVersionScope}
              showVersionScopeColumn={true}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box mb={3}>
            <ProductFeatureSuccessCriteriaSection
              productId={productId}
              featureId={featureId}
              readOnly={readOnly}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {!canManageRelations ? (
            <Typography variant="body2" color="text.secondary">
              Save the feature first to manage licenses/tags/resources/success criteria.
            </Typography>
          ) : (
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={3}>
              <TagManagementSection
                parentId={featureId ?? null}
                parentType={CacheKey.ProductFeature}
                disabled={!canManageRelations || readOnly}
              />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Licenses
                </Typography>

                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    select
                    size="small"
                    label="Attach license"
                    value={selectedLicense?.id ?? ""}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      setSelectedLicense(licensesById.get(id) || null);
                    }}
                    sx={{ flex: 1 }}
                    disabled={readOnly}
                  >
                    {(availableLicenses ?? []).map((l: any) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {!readOnly && (
                    <Button
                      variant="contained"
                      disabled={!selectedLicense}
                      onClick={() => handleAttachLicense(selectedLicense.id)}
                      size="small"
                    >
                      Attach
                    </Button>
                  )}
                </Box>

                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell width={70}>Remove</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {(featureLicenses ?? []).map((license: any) => (
                        <TableRow key={license.id}>
                          <TableCell>{license?.name ?? `#${license.id}`}</TableCell>
                          <TableCell>
                            {!readOnly && (
                              <IconButton size="small" onClick={() => handleDeleteLicense(license.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                      {(featureLicenses ?? []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2" color="text.secondary">
                              None
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {readOnly ? "Close" : "Cancel"}
          </Button>

          {!readOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !form.name || !hasUnsavedChanges}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {courseManagerOpen && (
        <SnackableCourseManager
          open={courseManagerOpen}
          entityType="product_feature"
          entityId={featureId}
          entityName={form.name}
          versionScope={effectiveVersionScope}
          onClose={() => setCourseManagerOpen(false)}
        />
      )}
    </>
  );
}